import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { usePortalContainer } from "@/shared/lib/view-mode";

/** Mobilon alulról felcsúszó panel, asztalon középre igazított párbeszéd
 *  (design-rendszer 6.: bottom sheet / dialógus). A mobilnézet keretében a keretbe nyílik,
 *  és ott — mint telefonon — alulról csúszik fel. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}) {
  const container = usePortalContainer();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal container={container ?? undefined}>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30" />
        <Dialog.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 max-h-[calc(var(--app-h)*0.88)] overflow-y-auto rounded-t-[16px] bg-white px-5 pb-[calc(20px+env(safe-area-inset-bottom))] pt-4",
            "md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:w-[560px] md:max-w-[92vw] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg md:p-6",
            className,
          )}
        >
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-line md:hidden" />
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-[19px] font-extrabold leading-tight">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="text-[13px] text-muted">
                  {description}
                </Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Bezárás"
              className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted hover:bg-paper"
            >
              <X size={20} />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
